import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { planPrice, PLANS } from "@/data/site";
import { toast } from "sonner";
import { X, ShieldCheck, TicketPercent, CreditCard, HandCoins } from "lucide-react";

const inputCls =
  "w-full bg-[#0D0E13] border border-white/10 rounded-2xl px-5 py-3.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-orange-500/70 transition-colors duration-300";

export function BookingModal({ selection, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user?.name || "", phone: "", address: "", city: "", date: "", notes: "" });
  const [promo, setPromo] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [booked, setBooked] = useState(null);

  const base = useMemo(() => (selection ? planPrice(selection.kw, selection.plan) : 0), [selection]);
  const final = promoApplied ? Math.round((base * (100 - promoApplied.pct)) / 100) : base;

  useEffect(() => {
    if (selection && user?.name) {
      setForm((f) => ({ ...f, name: f.name || user.name }));
    }
  }, [selection, user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const close = () => {
    setBooked(null);
    setError("");
    onClose();
  };

  const tryPromo = async () => {
    setPromoError("");
    try {
      const res = await api.get("/promo-codes/validate", { params: { code: promo } });
      if (res.data.valid) {
        setPromoApplied({ code: res.data.code, pct: res.data.discount_pct });
      } else {
        setPromoApplied(null);
        setPromoError("Codice non valido o non attivo");
      }
    } catch {
      setPromoError("Verifica del codice non riuscita. Riprova.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) {
      setError("Devi accettare la Privacy Policy per procedere.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/bookings", {
        ...form,
        kw: selection.kw,
        plan_type: selection.plan,
        promo_code: promoApplied?.code || "",
        privacy_consent: consent,
      });
      setBooked(res.data.booking);
      toast.success("Prenotazione registrata!");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const payNow = async () => {
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", {
        booking_id: booked.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Pagamento non disponibile ora."));
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <AnimatePresence>
      {selection && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto"
          onClick={close}
          data-testid="booking-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-[#13141B] border border-white/10 rounded-3xl p-7 sm:p-8 relative my-8 max-h-[90vh] overflow-y-auto"
            data-testid="booking-modal"
          >
            <button onClick={close} className="absolute top-5 right-5 text-slate-500 hover:text-white transition-colors" data-testid="booking-modal-close-button" aria-label="Chiudi">
              <X size={20} />
            </button>

            {booked ? (
              <div data-testid="booking-payment-step">
                <p className="font-mono-adj text-[10px] uppercase tracking-[0.3em] text-orange-500 font-semibold">Prenotazione Confermata</p>
                <h3 className="font-heading text-2xl font-extrabold text-white mt-2">
                  Come vuoi pagare?
                </h3>
                <p className="text-xs text-slate-400 mt-2">
                  Paga subito online in sicurezza, oppure scegli "da accordare": ti contattiamo noi e decidiamo insieme la modalità.
                </p>
                <div className="mt-5 bg-[#0D0E13] border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">{booked.kw} kW · {booked.plan_label}</span>
                    <span className="font-heading font-bold fire-text text-xl" data-testid="booking-final-price">€{booked.price}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
                    <ShieldCheck size={13} className="text-emerald-400" /> Data preferita: {booked.date} — {booked.city}
                  </p>
                  <p className="text-[11px] text-amber-400/90 mt-2" data-testid="booking-pending-confirm-note">
                    Resta in attesa: la data sarà confermata dal nostro team via email.
                  </p>
                </div>

                <button
                  onClick={payNow}
                  disabled={loading}
                  data-testid="booking-pay-now-button"
                  className="mt-5 w-full fire-bg text-white font-mono-adj text-xs uppercase tracking-[0.2em] font-semibold rounded-2xl px-6 py-5 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  <CreditCard size={18} />
                  {loading ? "Reindirizzamento..." : `Paga Ora Online — €${booked.price}`}
                </button>
                <p className="text-[11px] text-slate-500 text-center mt-2">Pagamento sicuro con Stripe · carta, Apple Pay, Google Pay · rateizzazione in arrivo sui contratti</p>

                <button
                  onClick={async () => {
                    try {
                      await api.post(`/bookings/${booked.id}/payment-choice`, { choice: "da_accordare" });
                    } catch {}
                    close();
                    navigate("/area-clienti");
                  }}
                  data-testid="booking-pay-later-button"
                  className="mt-3 w-full border border-white/15 text-slate-300 font-mono-adj text-xs uppercase tracking-[0.2em] rounded-2xl px-6 py-4 hover:border-orange-500/60 hover:text-orange-400 transition-colors flex items-center justify-center gap-3"
                >
                  <HandCoins size={17} />
                  Da Accordare con Voi
                </button>
                <p className="text-[11px] text-slate-500 text-center mt-4">
                  Scegliendo questa opzione ti contatteremo noi per confermare la prenotazione e concordare insieme la modalità di pagamento (bonifico, o anche saldo a lavoro completato).
                  Trovi tutto nella tua <Link to="/area-clienti" className="text-orange-400 underline underline-offset-2">Area Clienti</Link>.
                </p>
              </div>
            ) : (
              <>
                <p className="font-mono-adj text-[10px] uppercase tracking-[0.3em] text-orange-500 font-semibold">Prenotazione</p>
                <h3 className="font-heading text-2xl font-extrabold text-white mt-2">
                  {selection.kw} kW · {PLANS[selection.plan].short}
                </h3>

                <div className="mt-5 bg-[#0D0E13] border border-white/10 rounded-2xl p-5" data-testid="booking-price-summary">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">{PLANS[selection.plan].label}</span>
                    <span className={`font-heading font-bold ${promoApplied ? "text-slate-500 line-through" : "text-white"}`}>€{base}</span>
                  </div>
                  {promoApplied && (
                    <div className="flex items-center justify-between mt-2" data-testid="booking-promo-applied-row">
                      <span className="text-sm text-emerald-400 flex items-center gap-1.5"><TicketPercent size={14} /> {promoApplied.code} (-{promoApplied.pct}%)</span>
                      <span className="font-heading font-bold fire-text text-lg">€{final}</span>
                    </div>
                  )}
                  <div className="flex gap-2 mt-4">
                    <input
                      placeholder="Codice promo"
                      value={promo}
                      onChange={(e) => setPromo(e.target.value)}
                      className="flex-1 bg-[#13141B] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-orange-500/70 uppercase font-mono-adj tracking-widest"
                      data-testid="booking-promo-code-input"
                    />
                    <button
                      type="button"
                      onClick={tryPromo}
                      data-testid="booking-promo-apply-button"
                      className="border border-orange-500/50 text-orange-400 font-mono-adj text-[10px] uppercase tracking-[0.15em] rounded-xl px-4 hover:bg-orange-500/10 transition-colors"
                    >
                      Applica
                    </button>
                  </div>
                  {promoError && <p className="text-xs text-red-400 mt-2" data-testid="booking-promo-error">{promoError}</p>}
                  <p className="flex items-center gap-2 text-[11px] text-slate-500 mt-4">
                    <ShieldCheck size={13} className="text-emerald-400 shrink-0" />
                    Il pagamento avviene online in sicurezza con Stripe, anche a rate, oppure è da accordare con noi alla conferma della prenotazione.
                  </p>
                </div>

                <form onSubmit={submit} className="flex flex-col gap-3.5 mt-5" data-testid="booking-form">
                  <input required placeholder="Nome e Cognome" value={form.name} onChange={set("name")} className={inputCls} data-testid="booking-name-input" />
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <input required type="tel" placeholder="Telefono" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="booking-phone-input" />
                    <input required placeholder="Città" value={form.city} onChange={set("city")} className={inputCls} data-testid="booking-city-input" />
                  </div>
                  <input required placeholder="Indirizzo dell'impianto" value={form.address} onChange={set("address")} className={inputCls} data-testid="booking-address-input" />
                  <div>
                    <label className="font-mono-adj text-[9px] uppercase tracking-[0.25em] text-slate-500 block mb-2">Data preferita intervento</label>
                    <input required type="date" min={today} value={form.date} onChange={set("date")} className={inputCls} data-testid="booking-modal-date-picker" />
                    <p className="text-[11px] text-amber-400/90 mt-2 flex items-start gap-1.5" data-testid="booking-date-disclaimer">
                      La data è una preferenza: la conferma ufficiale dell'intervento ti arriverà sempre via email dal nostro team.
                    </p>
                  </div>
                  <textarea
                    rows={2}
                    placeholder="Note: accessibilità tetto, falda, presenza di ostacoli..."
                    value={form.notes}
                    onChange={set("notes")}
                    className={`${inputCls} resize-none`}
                    data-testid="booking-notes-input"
                  />

                  <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="booking-privacy-consent-label">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#FF6B00] cursor-pointer"
                      data-testid="booking-privacy-consent-checkbox"
                    />
                    <span className="text-xs text-slate-400 leading-relaxed">
                      Ho letto e accetto la{" "}
                      <Link to="/privacy" target="_blank" className="text-orange-400 underline underline-offset-2" data-testid="booking-privacy-link">
                        Privacy Policy
                      </Link>
                      . I miei dati saranno trattati da Proaura S.r.l.s. esclusivamente per gestire la prenotazione e non saranno ceduti a terzi.
                    </span>
                  </label>

                  {error && (
                    <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3" data-testid="booking-error-message">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="booking-modal-submit-button"
                    className="fire-bg text-white font-mono-adj text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-8 py-4 hover:opacity-90 active:scale-95 transition-all duration-300 disabled:opacity-50"
                  >
                    {loading ? "Invio in corso..." : `Conferma Prenotazione — €${final}`}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
